import { config } from '@/forms/config';
import { ValidationAdapter } from '@/forms/core';
import { FormFieldType } from '@/forms/typings';
import type {
  FormConditionalValidation,
  FormValidations,
  FormValidationSchema,
  IFormCheckbox,
  IFormTime,
} from '@/forms/typings';
import { generateConditionalValidation } from './generateConditionalValidation';
import { generateConditionalValidationFieldIds } from './generateConditionalValidationFieldIds';
import { officeHoursRangeIsValid } from './officeHoursRange';

/** Shown when a day's end time is not later than its start time. */
export const OFFICE_HOURS_RANGE_MESSAGE =
  'End time must be later than start time.';

/**
 * Yup schema for a day's `To` key: the end time must be later than the start time.
 * Skipped when the day is On Call or Closed, and gated by the field's conditions
 * (should-validate, N/A, QWD branch) like every other rule.
 */
export const createOfficeHoursRangeYupSchema = (
  fromFieldId: string,
  onCallFieldId: string | undefined,
  closedFieldId: string | undefined,
  conditions: FormConditionalValidation[] | undefined,
): FormValidationSchema => {
  const Yup = ValidationAdapter.getLibrary();
  const base = Yup.string().nullable();

  const tested = base.test({
    name: 'officeHoursRange',
    message: OFFICE_HOURS_RANGE_MESSAGE,
    test(value) {
      const root = this.parent as Record<string, unknown> | undefined;
      if (!root) return true;

      if (onCallFieldId && root[onCallFieldId] === true) return true;
      if (closedFieldId && root[closedFieldId] === true) return true;

      return officeHoursRangeIsValid(
        root[fromFieldId] as string | undefined,
        value,
      );
    },
  });

  if (!conditions?.length) return tested as unknown as FormValidationSchema;

  return base.when(
    generateConditionalValidationFieldIds(conditions),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (values: any[], schema: any) =>
      generateConditionalValidation(conditions, values) ? tested : schema,
  ) as unknown as FormValidationSchema;
};

/**
 * Registers the range rule on a day's `To` key. Keyed on the real field rather than a
 * synthetic one so InputTime renders the message under the offending box and Formik's
 * own gate blocks Continue.
 *
 * Safe to assign because time fields carry no `validations` of their own, so the
 * caller's generateValidations loop never writes this key. Compose the two schemas
 * instead of overwriting if that ever changes.
 */
export const registerOfficeHoursRangeValidation = (
  validationFields: FormValidations,
  dayFields: Array<IFormTime | IFormCheckbox>,
  conditions: FormConditionalValidation[] | undefined,
): void => {
  const from = dayFields.find(
    dayField =>
      dayField.type === FormFieldType.Time &&
      dayField.fieldId.endsWith(config.officeHoursFromFieldIdentifier),
  );
  const to = dayFields.find(
    dayField =>
      dayField.type === FormFieldType.Time &&
      dayField.fieldId.endsWith(config.officeHoursToFieldIdentifier),
  );

  if (!from || !to) return;

  const onCall = dayFields.find(
    dayField =>
      dayField.type === FormFieldType.Checkbox &&
      dayField.fieldId.endsWith(config.officeHoursOnCallFieldSuffix),
  );
  const closed = dayFields.find(
    dayField =>
      dayField.type === FormFieldType.Checkbox &&
      dayField.fieldId.endsWith(config.officeHoursClosedFieldIdentifier),
  );

  validationFields[to.fieldId] = createOfficeHoursRangeYupSchema(
    from.fieldId,
    onCall?.fieldId,
    closed?.fieldId,
    conditions,
  );
};
